import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Ic } from '../../design/icons';
import { Avatar, Chip } from '../../design/primitives';

const API = `${window.location.origin}/api`;

const ROLE_LABELS = {
  super_admin: 'Süper Admin',
  admin: 'Admin',
  proje_yoneticisi: 'Proje Yöneticisi',
  takim_lideri: 'Takım Lideri',
  takim_uyesi: 'Takım Üyesi',
  yorumcu: 'Yorumcu',
};

const ROLE_COLOR = {
  super_admin: '#b45309',
  admin: 'var(--danger)',
  proje_yoneticisi: '#6c4ca9',
  takim_lideri: 'var(--accent)',
  takim_uyesi: 'var(--good)',
  yorumcu: 'var(--text-muted)',
};

// WorkspaceMember.role artık owner + admin/proje_yoneticisi/takim_lideri/
// takim_uyesi vokabülerini kullanır; her workspace için ayrıdır.
const WS_MEMBER_LABELS = {
  owner: 'Sahip',
  admin: 'Admin',
  proje_yoneticisi: 'Proje Yöneticisi',
  takim_lideri: 'Takım Lideri',
  takim_uyesi: 'Takım Üyesi',
};
const WS_MEMBER_COLOR = {
  owner: '#b45309',
  admin: 'var(--danger)',
  proje_yoneticisi: '#6c4ca9',
  takim_lideri: 'var(--accent)',
  takim_uyesi: 'var(--good)',
};

const STATUS_LABELS = {
  active: 'Aktif',
  pending_verification: 'E-posta Bekliyor',
  suspended: 'Askıda',
  deleted: 'Silindi',
};

const PLAN_LABELS = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  unlimited: 'Sınırsız',
};

function statusChip(status) {
  if (status === 'suspended' || status === 'deleted')
    return <Chip color="var(--warning)" bg="var(--warning-weak)">{STATUS_LABELS[status]}</Chip>;
  if (status === 'pending_verification')
    return <Chip color="var(--accent)" bg="var(--surface-muted)">{STATUS_LABELS[status]}</Chip>;
  return <Chip color="var(--good)" bg="var(--good-weak)">{STATUS_LABELS.active}</Chip>;
}

export default function GlobalUsers() {
  usePageHeader([{ label: 'Süper Admin' }, { label: 'Tüm Kullanıcılar' }], null, []);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterWorkspace, setFilterWorkspace] = useState('all');
  const [hasSubOnly, setHasSubOnly] = useState(false);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', role: '', status: '' });
  const [saving, setSaving] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);

  const [resetPwTarget, setResetPwTarget] = useState(null);
  const [resetPwMode, setResetPwMode] = useState('auto');
  const [resetPwManual, setResetPwManual] = useState('');
  const [resetPwGenerated, setResetPwGenerated] = useState('');
  const [resetPwSubmitting, setResetPwSubmitting] = useState(false);

  // Hard-delete akışı: kullanıcı bir veya birden fazla workspace'in
  // sahibiyse, her biri için "delete" veya "transfer" stratejisi sorulur.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  // Şekil: { [workspaceId]: { action: 'delete' | 'transfer', targetUserId?: string } }
  const [deleteStrategies, setDeleteStrategies] = useState({});
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced, filterRole, filterStatus, filterPlan, filterWorkspace, hasSubOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWorkspacesLoading(true);
      try {
        const res = await axios.get(`${API}/super-admin/workspaces?limit=500`);
        if (!cancelled) setWorkspaceOptions(res.data.workspaces || []);
      } catch {
        if (!cancelled) setWorkspaceOptions([]);
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (debounced) params.append('q', debounced);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPlan !== 'all') params.append('plan', filterPlan);
      if (filterWorkspace !== 'all') params.append('workspace_id', filterWorkspace);
      if (hasSubOnly) params.append('has_subscription', 'true');
      const res = await axios.get(`${API}/super-admin/users?${params.toString()}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, debounced, filterRole, filterStatus, filterPlan, filterWorkspace, hasSubOnly]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEdit = (u) => {
    setEditTarget(u);
    setEditDraft({ name: u.name || '', role: u.role || 'takim_uyesi', status: u.status || 'active' });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const payload = {};
      if (editDraft.name && editDraft.name !== editTarget.name) payload.name = editDraft.name;
      if (editDraft.role !== editTarget.role) payload.role = editDraft.role;
      if (editDraft.status !== editTarget.status) payload.status = editDraft.status;
      if (Object.keys(payload).length === 0) {
        setEditTarget(null);
        return;
      }
      await axios.patch(`${API}/super-admin/users/${editTarget.id}`, payload);
      toast.success('Kullanıcı güncellendi');
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    setSaving(true);
    try {
      await confirmAction.run();
      toast.success(confirmAction.successMessage);
      setConfirmAction(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = (u) => {
    const willSuspend = u.status !== 'suspended';
    setConfirmAction({
      title: willSuspend ? 'Hesabı Dondur' : 'Hesabı Aktifleştir',
      message: willSuspend
        ? `${u.name || u.email} hesabı dondurulacak. Açık oturumları kapatılacak ve giriş yapamayacak. Devam edilsin mi?`
        : `${u.name || u.email} hesabı tekrar aktifleştirilecek. Devam edilsin mi?`,
      confirmLabel: willSuspend ? 'Hesabı Dondur' : 'Aktifleştir',
      successMessage: willSuspend ? 'Hesap donduruldu' : 'Hesap aktifleştirildi',
      run: () => axios.patch(`${API}/super-admin/users/${u.id}`, {
        status: willSuspend ? 'suspended' : 'active',
      }),
      danger: willSuspend,
    });
  };

  const hardDelete = async (u) => {
    setDeleteTarget(u);
    setDeleteStrategies({});
    setDeleteImpact(null);
    setDeleteImpactLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/users/${u.id}/delete-impact`);
      setDeleteImpact(res.data);
      // Sahip olunan her workspace için varsayılan strateji: delete (en
      // güvenli — ekstra hedef seçimi gerekmez). Kullanıcı transfer'e
      // geçmek isterse aşağıdaki UI'dan değiştirir.
      const initialStrategies = {};
      for (const ws of res.data?.ownedWorkspaces || []) {
        initialStrategies[ws.id] = { action: 'delete', targetUserId: '' };
      }
      setDeleteStrategies(initialStrategies);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Etki bilgisi alınamadı');
      setDeleteTarget(null);
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteTarget(null);
    setDeleteImpact(null);
    setDeleteStrategies({});
  };

  const submitHardDelete = async () => {
    if (!deleteTarget) return;
    // Transfer seçeneklerinde hedef kullanıcı seçilmiş mi doğrula.
    for (const ws of deleteImpact?.ownedWorkspaces || []) {
      const s = deleteStrategies[ws.id];
      if (s?.action === 'transfer' && !s.targetUserId) {
        toast.error(`"${ws.name}" için sahiplik devredilecek kullanıcıyı seçin.`);
        return;
      }
    }
    setDeleteSubmitting(true);
    try {
      const ownedStrategy = {};
      for (const [wid, s] of Object.entries(deleteStrategies)) {
        ownedStrategy[wid] = s.action === 'transfer'
          ? { action: 'transfer', targetUserId: s.targetUserId }
          : { action: 'delete' };
      }
      await axios.delete(`${API}/super-admin/users/${deleteTarget.id}`, {
        data: { ownedStrategy },
      });
      toast.success('Kullanıcı kalıcı olarak silindi');
      closeDeleteModal();
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleResendVerification = async (email) => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Doğrulama maili gönderildi');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Mail gönderilemedi');
    }
  };

  const openResetPassword = (u) => {
    setResetPwTarget(u);
    setResetPwMode('auto');
    setResetPwManual('');
    setResetPwGenerated('');
  };

  const handleResetPassword = async () => {
    if (!resetPwTarget) return;
    setResetPwSubmitting(true);
    try {
      const body = resetPwMode === 'manual' && resetPwManual
        ? { password: resetPwManual }
        : null;
      await axios.post(`${API}/admin/users/${resetPwTarget.id}/reset-password`, body);
      setResetPwGenerated(resetPwMode === 'manual' ? resetPwManual : '');
      toast.success('Şifre sıfırlandı. Yeni şifre kullanıcının e-postasına gönderildi.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Şifre sıfırlanamadı');
    } finally {
      setResetPwSubmitting(false);
    }
  };

  const copyResetPw = () => {
    navigator.clipboard.writeText(resetPwGenerated);
    toast.success('Şifre panoya kopyalandı');
  };

  const filtersActive = debounced || filterRole !== 'all' || filterStatus !== 'all' || filterPlan !== 'all' || filterWorkspace !== 'all' || hasSubOnly;

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="ox-grid-stats-6">
        <StatTile label="Toplam (filtreli)" value={total} />
        <StatTile label="Sayfa" value={`${page} / ${totalPages || 1}`} small />
        <StatTile label="Süper Adminler" value={users.filter(u => u.role === 'super_admin').length} color="#b45309" />
        <StatTile label="Adminler" value={users.filter(u => u.role === 'admin').length} color="var(--danger)" />
        <StatTile label="Aboneli" value={users.filter(u => u.activePlan).length} color="var(--accent)" />
        <StatTile label="Pasif/Askıda" value={users.filter(u => u.status !== 'active').length} color="var(--warning)" />
      </div>

      <div className="ox-surface rounded-md p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[260px] sm:flex-none basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
          <input
            className="ox-input pl-8"
            placeholder="İsim veya e-posta ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="ox-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="all">Tüm Roller</option>
          {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ox-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ maxWidth: 170 }}>
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="pending_verification">E-posta Bekliyor</option>
          <option value="suspended">Askıda</option>
          <option value="deleted">Silindi</option>
        </select>
        <select className="ox-select" value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} style={{ maxWidth: 150 }}>
          <option value="all">Tüm Planlar</option>
          {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          className="ox-select"
          value={filterWorkspace}
          onChange={(e) => setFilterWorkspace(e.target.value)}
          style={{ maxWidth: 220 }}
          disabled={workspacesLoading}
          title="Workspace'e göre filtrele"
        >
          <option value="all">{workspacesLoading ? 'Workspace yükleniyor...' : 'Tüm Workspaceler'}</option>
          {workspaceOptions.map((w) => (
            <option key={w.id} value={w.id}>{w.name || '(isimsiz)'}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-[12.5px] ox-text-muted">
          <input type="checkbox" checked={hasSubOnly} onChange={(e) => setHasSubOnly(e.target.checked)} />
          Sadece aboneliği olan
        </label>
        {filtersActive && (
          <button
            className="ox-btn ox-btn-ghost"
            onClick={() => {
              setSearch('');
              setFilterRole('all');
              setFilterStatus('all');
              setFilterPlan('all');
              setFilterWorkspace('all');
              setHasSubOnly(false);
            }}
          >
            {Ic.x} Temizle
          </button>
        )}
        <button className="ox-btn ox-btn-ghost ml-auto" onClick={fetchUsers} title="Yenile">
          {Ic.refresh}
        </button>
      </div>

      <div className="ox-surface rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center ox-text-muted">Yükleniyor...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center ox-text-muted">Kullanıcı bulunamadı</div>
        ) : (
          <div className="ox-table-wrap ox-table-wide">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Kullanıcı</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Rol</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Durum</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">E-posta Doğrulama</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Workspace</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">Plan</th>
                  <th className="text-right px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar u={u} imgSrc={u.picture} size={28} />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium truncate">{u.name}</div>
                          <div className="text-[11px] ox-text-muted truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {u.role === 'super_admin' ? (
                        <Chip color={ROLE_COLOR.super_admin} bg="var(--surface-muted)">
                          {ROLE_LABELS.super_admin}
                        </Chip>
                      ) : (() => {
                        // Workspace bazlı rol gösterimi: çip etiketi her
                        // workspace için ayrı olan WorkspaceMember.role'tan
                        // gelir. Kullanıcı bir WS'te Takım Lideri, başka bir
                        // WS'te Proje Yöneticisi olabilir. Owner ise "Sahip".
                        // Workspace filtresi seçiliyse sadece o WS'nin çipi.
                        const visible = filterWorkspace !== 'all'
                          ? (u.workspaces || []).filter(w => w.id === filterWorkspace)
                          : (u.workspaces || []);
                        if (visible.length === 0) {
                          return <span className="ox-text-dim text-[12px]">—</span>;
                        }
                        return (
                          <div className="flex flex-wrap items-center gap-1" style={{ maxWidth: 280 }}>
                            {visible.map(w => {
                              const role = w.role || 'takim_uyesi';
                              return (
                                <Chip
                                  key={w.id}
                                  color={WS_MEMBER_COLOR[role] || 'var(--text-muted)'}
                                  bg="var(--surface-muted)"
                                  title={w.name || ''}
                                >
                                  {WS_MEMBER_LABELS[role] || role}
                                  {filterWorkspace === 'all' && visible.length > 1 && w.name && (
                                    <span className="ox-text-dim" style={{ marginLeft: 4, fontSize: 10 }}>
                                      · {w.name}
                                    </span>
                                  )}
                                </Chip>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5">{statusChip(u.status)}</td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      {u.emailVerified ? (
                        <Chip color="var(--good)" bg="var(--good-weak)">{Ic.check} Doğrulandı</Chip>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Chip color="var(--warning)" bg="var(--warning-weak)">Doğrulanmadı</Chip>
                          <button className="ox-btn ox-btn-ghost p-1" title="Doğrulama mailini tekrar gönder"
                            onClick={() => handleResendVerification(u.email)}>
                            {Ic.refresh}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      {(u.workspaces && u.workspaces.length) ? (
                        <div className="flex items-center gap-1 flex-wrap" style={{ maxWidth: 260 }}>
                          <span
                            className="text-[12.5px] truncate"
                            style={{ maxWidth: 200 }}
                            title={u.workspaces.map(w => w.name || '(isimsiz)').join(', ')}
                          >
                            {u.workspaces[0].name || '(isimsiz)'}
                          </span>
                          {u.workspaces.length > 1 && (
                            <Chip color="var(--text-muted)" bg="var(--surface-muted)">+{u.workspaces.length - 1}</Chip>
                          )}
                        </div>
                      ) : (
                        <span className="ox-text-dim text-[12px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      {u.activePlan ? (
                        <Chip color="var(--accent)" bg="var(--surface-muted)">{PLAN_LABELS[u.activePlan] || u.activePlan}</Chip>
                      ) : (
                        <span className="ox-text-dim text-[12px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-0.5 justify-end">
                        <button className="ox-btn ox-btn-ghost p-1.5" title="Düzenle" onClick={() => openEdit(u)}>{Ic.edit}</button>
                        <button className="ox-btn ox-btn-ghost p-1.5" title="Şifre Sıfırla" onClick={() => openResetPassword(u)}>{Ic.key}</button>
                        <button
                          className="ox-btn ox-btn-ghost p-1.5"
                          title={u.status === 'suspended' ? 'Hesabı aktifleştir' : 'Hesabı dondur'}
                          style={{ color: u.status === 'suspended' ? 'var(--good)' : 'var(--danger)' }}
                          onClick={() => toggleSuspend(u)}
                        >
                          {u.status === 'suspended' ? Ic.check : Ic.pause}
                        </button>
                        <button
                          className="ox-btn ox-btn-ghost p-1.5"
                          title="Kalıcı olarak sil"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => hardDelete(u)}
                        >
                          {Ic.trash}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-[12px] ox-text-muted">
              {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} / {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                className="ox-btn ox-btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {Ic.chevronLeft || '‹'} Önceki
              </button>
              <button
                className="ox-btn ox-btn-ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Sonraki {Ic.chevronRight || '›'}
              </button>
            </div>
          </div>
        )}
      </div>

      {editTarget && (
        <div className="ox-modal-backdrop" onClick={() => !saving && setEditTarget(null)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Kullanıcıyı Düzenle</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setEditTarget(null)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-3">
              <div className="text-[12px] ox-text-muted">{editTarget.email}</div>
              <div className="ox-field">
                <label>Ad Soyad</label>
                <input
                  className="ox-input"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                />
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="ox-field">
                  <label>Rol</label>
                  <select
                    className="ox-select"
                    value={editDraft.role}
                    onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                  >
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="ox-field">
                  <label>Durum</label>
                  <select
                    className="ox-select"
                    value={editDraft.status}
                    onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                  >
                    <option value="active">Aktif</option>
                    <option value="pending_verification">E-posta Bekliyor</option>
                    <option value="suspended">Askıda</option>
                    <option value="deleted">Silindi</option>
                  </select>
                </div>
              </div>
              <div className="text-[11.5px] ox-text-muted">
                Rol veya durum değişiklikleri kullanıcının açık oturumlarını otomatik olarak kapatır.
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={saving} onClick={() => setEditTarget(null)}>İptal</button>
              <button className="ox-btn" disabled={saving} onClick={saveEdit}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetPwTarget && (
        <div className="ox-modal-backdrop" onClick={() => !resetPwSubmitting && setResetPwTarget(null)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Şifre Sıfırla: {resetPwTarget.name || resetPwTarget.email}</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setResetPwTarget(null)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-3">
              <div className="text-[12px] ox-text-muted">{resetPwTarget.email}</div>
              <div className="flex gap-2">
                <button
                  className={`ox-btn ${resetPwMode === 'auto' ? 'ox-btn-primary' : ''} flex-1`}
                  onClick={() => setResetPwMode('auto')}
                >
                  Otomatik
                </button>
                <button
                  className={`ox-btn ${resetPwMode === 'manual' ? 'ox-btn-primary' : ''} flex-1`}
                  onClick={() => setResetPwMode('manual')}
                >
                  Manuel
                </button>
              </div>
              {resetPwMode === 'manual' && (
                <div className="ox-field">
                  <label>Yeni Şifre</label>
                  <input type="text" className="ox-input" value={resetPwManual}
                    onChange={(e) => setResetPwManual(e.target.value)}
                    minLength={6} placeholder="En az 6 karakter" />
                </div>
              )}
              {resetPwGenerated && (
                <div className="flex items-center gap-2 p-2 rounded" style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)' }}>
                  <code className="ox-mono text-[12.5px] flex-1 truncate">{resetPwGenerated}</code>
                  <button className="ox-btn p-1" onClick={copyResetPw} title="Kopyala">{Ic.copy}</button>
                </div>
              )}
              <p className="text-[11.5px] ox-text-muted">
                Yeni şifre kullanıcının e-posta adresine gönderilecek.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={resetPwSubmitting} onClick={() => setResetPwTarget(null)}>Kapat</button>
              <button
                className="ox-btn ox-btn-primary"
                onClick={handleResetPassword}
                disabled={resetPwSubmitting || (resetPwMode === 'manual' && resetPwManual.length < 6)}
              >
                {resetPwSubmitting ? 'Sıfırlanıyor...' : 'Şifreyi Sıfırla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="ox-modal-backdrop" onClick={() => !saving && setConfirmAction(null)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">{confirmAction.title}</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setConfirmAction(null)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px]" style={{ color: 'var(--text)' }}>{confirmAction.message}</p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={saving} onClick={() => setConfirmAction(null)}>İptal</button>
              <button
                className={confirmAction.danger ? 'ox-btn ox-btn-danger' : 'ox-btn'}
                disabled={saving}
                onClick={runConfirmAction}
              >
                {saving ? 'Çalışıyor...' : confirmAction.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ox-modal-backdrop" onClick={closeDeleteModal}>
          <div
            className="ox-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-hard-delete-modal"
            style={{ maxWidth: 560, width: 'min(560px, 96vw)' }}
          >
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]" style={{ color: 'var(--danger)' }}>
                Kullanıcıyı Kalıcı Olarak Sil
              </div>
              <button
                className="ox-btn ox-btn-ghost p-1"
                onClick={closeDeleteModal}
                disabled={deleteSubmitting}
              >{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px] mb-2" style={{ color: 'var(--text)' }}>
                <strong>{deleteTarget.name || deleteTarget.email}</strong> hesabı ve tüm bağlı kayıtları (üyelikler, davetler, oturumlar, takım/proje atamaları) kalıcı olarak silinecek.
              </p>

              {deleteImpactLoading ? (
                <p className="text-[12px] ox-text-muted">Sahip olunan workspace'ler kontrol ediliyor…</p>
              ) : (deleteImpact?.ownedWorkspaces?.length ?? 0) === 0 ? (
                <p className="text-[12px] ox-text-muted">
                  Bu kullanıcı hiçbir workspace'in sahibi değil. Doğrudan silinebilir.
                </p>
              ) : (
                <>
                  <div
                    className="text-[12px]"
                    style={{
                      background: 'var(--surface-muted)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: 10,
                      marginBottom: 12,
                      lineHeight: 1.55,
                      color: 'var(--text)',
                    }}
                  >
                    Kullanıcı <strong>{deleteImpact.ownedWorkspaces.length}</strong> workspace'in sahibi.
                    Her biri için ne yapılacağını seçin.
                  </div>

                  {deleteImpact.ownedWorkspaces.map((ws) => {
                    const strat = deleteStrategies[ws.id] || { action: 'delete', targetUserId: '' };
                    return (
                      <div
                        key={ws.id}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 10,
                          background: 'var(--surface)',
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-[13px] font-semibold truncate">{ws.name || ws.id}</div>
                            <div className="text-[11.5px] ox-text-muted">
                              {ws.stats?.members ?? 0} üye · {ws.stats?.projects ?? 0} proje · {ws.stats?.tasks ?? 0} görev
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1.5 mt-2.5">
                          <label
                            className="flex items-start gap-2"
                            style={{
                              padding: 8, borderRadius: 6, cursor: 'pointer',
                              background: strat.action === 'delete' ? 'var(--accent-weak)' : 'transparent',
                              border: '1px solid var(--border)',
                            }}
                          >
                            <input
                              type="radio"
                              name={`strat-${ws.id}`}
                              checked={strat.action === 'delete'}
                              onChange={() => setDeleteStrategies(s => ({
                                ...s, [ws.id]: { action: 'delete', targetUserId: '' },
                              }))}
                              style={{ marginTop: 2 }}
                            />
                            <div>
                              <div className="text-[12.5px] font-medium">Workspace'i tamamen sil</div>
                              <div className="text-[11.5px] ox-text-muted">
                                Tüm görev, dosya, yorum, üyelik ve abonelik kalıcı olarak silinir.
                              </div>
                            </div>
                          </label>

                          <label
                            className="flex items-start gap-2"
                            style={{
                              padding: 8, borderRadius: 6,
                              cursor: ws.transferCandidates.length ? 'pointer' : 'not-allowed',
                              opacity: ws.transferCandidates.length ? 1 : 0.5,
                              background: strat.action === 'transfer' ? 'var(--accent-weak)' : 'transparent',
                              border: '1px solid var(--border)',
                            }}
                          >
                            <input
                              type="radio"
                              name={`strat-${ws.id}`}
                              disabled={!ws.transferCandidates.length}
                              checked={strat.action === 'transfer'}
                              onChange={() => setDeleteStrategies(s => ({
                                ...s, [ws.id]: { action: 'transfer', targetUserId: '' },
                              }))}
                              style={{ marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="text-[12.5px] font-medium">Sahipliği başka kullanıcıya devret</div>
                              <div className="text-[11.5px] ox-text-muted">
                                {ws.transferCandidates.length
                                  ? 'Workspace ve içeriği korunur; seçilen kullanıcı yeni sahip olur.'
                                  : 'Bu workspace\'te devredilecek aktif başka üye yok.'}
                              </div>
                              {strat.action === 'transfer' && ws.transferCandidates.length > 0 && (
                                <select
                                  className="ox-select mt-2"
                                  value={strat.targetUserId}
                                  onChange={(e) => setDeleteStrategies(s => ({
                                    ...s, [ws.id]: { action: 'transfer', targetUserId: e.target.value },
                                  }))}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ width: '100%' }}
                                >
                                  <option value="">— Yeni sahip seçin —</option>
                                  {ws.transferCandidates.map(c => (
                                    <option key={c.userId} value={c.userId}>
                                      {c.name || c.email} ({WS_MEMBER_LABELS[c.currentRole] || c.currentRole})
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              <p className="text-[11.5px] ox-text-muted mt-2">
                Bu işlem geri alınamaz.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={closeDeleteModal}
                disabled={deleteSubmitting}
              >İptal</button>
              <button
                className="ox-btn ox-btn-danger"
                onClick={submitHardDelete}
                disabled={deleteSubmitting || deleteImpactLoading}
                data-testid="user-hard-delete-confirm"
              >
                {deleteSubmitting ? 'İşleniyor…' : 'Kalıcı Olarak Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color, small }) {
  return (
    <div className="ox-surface rounded-md p-3">
      <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{label}</div>
      <div
        className="ox-tnum font-semibold mt-1"
        style={{ color: color || 'var(--text)', fontSize: small ? 16 : 22 }}
      >
        {value}
      </div>
    </div>
  );
}
